"use client";

import { useEffect, useRef, useState } from "react";

export interface Suggestion {
  value: string;
  unrestricted_value?: string;
  data?: Record<string, unknown>;
}

interface Props {
  kind: "party" | "address" | "fio" | "email";
  value: string;
  onChange: (v: string) => void;
  onPick?: (s: Suggestion) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  // Extra payload merged into the POST body sent to DaData (e.g. locations
  // or parts filter). Kept as a plain object; stringified at request time.
  payload?: Record<string, unknown>;
  // Number of characters required before firing a request. Default 2.
  minChars?: number;
  // Debounce in ms. Default 250.
  debounceMs?: number;
  // Called on raw input change (applies masks/filters in the caller).
  filter?: (raw: string) => string;
}

export function SuggestInput({
  kind,
  value,
  onChange,
  onPick,
  placeholder,
  style,
  inputStyle,
  payload,
  minChars = 2,
  debounceMs = 250,
  filter,
}: Props) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!value || value.length < minChars) { setList([]); return; }
    const id = setTimeout(async () => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      try {
        setLoading(true);
        const body: Record<string, unknown> = { query: value, count: 7, ...(payload || {}) };
        const r = await fetch(`/api/v1/suggest/${kind}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: abortRef.current.signal,
        });
        if (!r.ok) { setList([]); return; }
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) { setList([]); return; }
        const data = await r.json().catch(() => ({}));
        setList(Array.isArray(data.suggestions) ? data.suggestions : []);
      } catch { /* ignore */ } finally { setLoading(false); }
    }, debounceMs);
    return () => clearTimeout(id);
  }, [value, kind, minChars, debounceMs, JSON.stringify(payload ?? {})]);

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", ...style }}>
      <input
        value={value}
        onChange={(e) => { const raw = e.target.value; onChange(filter ? filter(raw) : raw); setOpen(true); }}
        onFocus={() => setOpen(true)}
        // Закрыть подсказки при потере фокуса (Tab, переход в соседний инпут
        // и т.п.). Небольшая задержка, чтобы клик по предложению в списке
        // успел отработать до того как мы свернём dropdown.
        onBlur={() => { setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder}
        style={{
          color: "#000", fontSize: 15, background: "#fff",
          border: "1px solid #E0E0E5", padding: "10px 12px", width: "100%",
          boxSizing: "border-box", fontFamily: "inherit", outline: "none",
          borderRadius: 0,
          ...(inputStyle || {}),
        }}
      />
      {open && list.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30,
          background: "#fff", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          maxHeight: 300, overflowY: "auto",
        }}>
          {list.map((s, i) => (
            <div
              key={`${s.value}-${i}`}
              onClick={() => { onChange(s.value); onPick?.(s); setOpen(false); }}
              style={{ padding: "10px 14px", fontSize: 14, cursor: "pointer", lineHeight: 1.3, wordBreak: "break-word", borderBottom: i < list.length - 1 ? "1px solid #F2F2F5" : undefined }}
            >
              {s.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
